import { SETVI_LOGO_URL } from '@setvi/shared/enums';

import { pluralize } from '@setvi/shared/utils';

import { COVER_FIELD_KEYS } from '../constants';
import type { CoverForm, PrintOrderItem } from '../types';

// Standalone markup for the checkout tab. It is a blank window we write into,
// not part of the app, so the loader is reproduced here rather than rendered:
// the same SETVI mark, fade and white ground as the loader shown while the app
// boots, with the portal message under it.
const connectingDocument = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Print portal</title>
<style>
  body {
    margin: 0;
    min-height: 100vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 40px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
      Helvetica, Arial, sans-serif;
    color: #5D7083;
  }
  .logo {
    width: 15%;
    min-width: 180px;
    max-width: 320px;
    animation: fade-in 1s ease-in 1;
  }
  .label {
    font-size: 16px;
  }
  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    .logo { animation: none; }
  }
</style>
</head>
<body>
  <img class="logo" src="${SETVI_LOGO_URL}" alt="SETVI">
  <div class="label">Connecting to the print portal…</div>
</body>
</html>`;

// Opens the checkout tab synchronously (inside the click's user gesture — the
// ONLY reliable way to open a tab: window.open after an await gets silently
// dropped by popup blockers, Brave in particular). The tab shows the loader
// until the checkout response points it at the portal via `redirect`; `close`
// removes it when checkout fails or returns no url.
export const openCheckoutTab = () => {
  const tab = window.open('', '_blank');
  if (tab) {
    tab.document.open();
    tab.document.write(connectingDocument);
    tab.document.close();
  }

  return {
    redirect: (url: string) => {
      if (tab) {
        // Drop the opener reference for the same safety as noopener.
        tab.opener = null;
        tab.location.href = url;
      } else {
        // The tab was blocked anyway — try a direct open as a last resort.
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    close: () => tab?.close()
  };
};

/**
 * Cover details for a request body. Only fields with a value are sent, so the
 * backend can fall back to the profile; a field the rep cleared is sent as ''
 * so that line prints blank, and one they never touched is left out.
 */
export const buildCoverFieldsBody = (
  fields?: CoverForm
): Record<string, string> => {
  if (!fields) return {};

  const body: Record<string, string> = {};

  COVER_FIELD_KEYS.forEach(key => {
    const value = fields.values[key]?.trim() ?? '';
    if (value) body[key] = value;
    else if (fields.touched.includes(key)) body[key] = '';
  });

  // Undefined keeps the current logo, '' removes it. The data: prefix is
  // accepted by the generator, which strips it itself.
  if (fields.logoBase64 !== undefined) body.LogoBase64 = fields.logoBase64;

  return body;
};

export const parseMachineNumbers = (value: string): string[] =>
  Array.from(
    new Set(
      value
        .split(/[\s,]+/)
        .map(part => part.trim())
        .filter(Boolean)
    )
  );

export const isItemCustomized = (item: PrintOrderItem): boolean =>
  item.addQr || item.addCover || !!item.addLocation;

// One QR page per machine number.
export const getItemMachineCount = (item: PrintOrderItem): number =>
  item.addQr ? parseMachineNumbers(item.qrNumbers).length : 0;

// Page count used for the "N pages total" footer.
export const getItemPageCount = (item: PrintOrderItem): number => {
  const machines = getItemMachineCount(item);
  if (item.addQr && machines > 0) return machines;
  return item.resource.Pages || 1;
};

// Label shown on the cart item, e.g. "8 QR pages" or "4 pages".
export const getItemPagesLabel = (item: PrintOrderItem): string => {
  const machines = getItemMachineCount(item);
  if (item.addQr && machines > 0) return pluralize(machines, 'QR page');
  return pluralize(item.resource.Pages || 1, 'page');
};
